import { apiFetch } from "@/lib/api";
import { trimText, trimTextOrNull } from "@/lib/utils";
import {
  buildMrpSkuCatalogLineDescription,
  defaultPurchaseOrderTransferDestinationPublicLocationId,
  defaultPurchaseOrderTransferOriginPublicLocationId,
  type MrpLocation,
  type MrpPurchaseOrder,
  type MrpPurchaseOrderLine,
  type MrpSkuDetail,
  type MrpSkuHoverHintFields,
  type MrpTransferReason,
} from "@/lib/mrp";
import {
  formatServiceSkuUnitPriceAutofill,
  isPurchaseOrderServiceSku,
} from "@integrator-src/mrp/purchase-order-service-sku-price";
import {
  parsePurchaseOrderAmountRoundingMode,
  purchaseOrderOrderTotal,
  type PurchaseOrderAmountRoundingMode,
} from "@integrator-src/mrp/purchase-order-amount-rounding";
import {
  assignSkuSplitGroupIdsForDuplicateSkus,
  findDraftAccidentalDuplicateSkuLineIndices,
  normalizePurchaseOrderSku,
} from "@/lib/purchaseOrderLineGroups";

export interface MvPoLineTransferRequestForm {
  client_id: string;
  quantity: string;
  destination_location_id: string;
  target_date: string;
  reason: MrpTransferReason;
  notes: string;
}

export interface MvPoLineForm {
  client_id: string;
  /** Lines created via + share a group id; same SKU on unrelated rows is a validation error. */
  sku_split_group_id: string | null;
  sku: string;
  line_description: string;
  quantity: string;
  unit_price: string;
  mv_row_detail_id: string;
  expected_delivery_date: string;
  transfer_requests: MvPoLineTransferRequestForm[];
  /** Saved Megaventory PO line (`public.purchase_order_item.id`). */
  purchase_order_item_id?: number;
  /** Saved PO: `mrp.purchase_order_item_delivery.id` child row. */
  delivery_segment_id?: number;
  is_delivery_segment?: boolean;
  quantity_received?: number;
  quantity_invoiced?: number;
  quantity_invoiced_total?: number;
  receiving_status?: MrpPurchaseOrderLine["status"];
  sku_catalog_description?: string | null;
  /** Catalog unit for display; refreshed when the SKU changes on draft lines. */
  mv_unit?: string | null;
}

export interface PurchaseOrderSubmitLine {
  sku: string;
  quantity: number;
  unit_price: number | null;
  line_description?: string | null;
  mv_row_detail_id?: number;
  expected_delivery_date?: string | null;
  transfer_requests?: {
    quantity: number;
    origin_location_id: number;
    destination_location_id: number;
    target_date: string;
    reason: MrpTransferReason;
    notes: string | null;
  }[];
}

export interface PurchaseOrderDraftLineResponse {
  sku: string;
  quantity: number;
  unit_price: number;
  mv_row_detail_id: number;
  line_description?: string | null;
  expected_delivery_date?: string | null;
}

export const PURCHASE_ORDER_CURRENCIES = [
  { value: "USD", label: "USD" },
  { value: "CNY", label: "CNY" },
] as const;

export type PurchaseOrderCurrency = (typeof PURCHASE_ORDER_CURRENCIES)[number]["value"];

export function newTransferRequestClientId(): string {
  return crypto.randomUUID();
}

export function newDraftLineClientId(): string {
  return crypto.randomUUID();
}

export function defaultTransferRequestForLine(
  line: MvPoLineForm,
  locationCode: string,
): MvPoLineTransferRequestForm {
  return {
    client_id: newTransferRequestClientId(),
    quantity: trimText(line.quantity) !== "" ? trimText(line.quantity) : "1",
    destination_location_id: String(defaultPurchaseOrderTransferDestinationPublicLocationId(locationCode)),
    target_date: trimText(line.expected_delivery_date),
    reason: "upcoming_needs",
    notes: "",
  };
}

/** Normalize compose draft lines (including legacy single-transfer fields) into the current form shape. */
export function normalizeMvPoLineForm(
  raw: Partial<MvPoLineForm> & Record<string, unknown>,
  locationCode: string,
): MvPoLineForm {
  const base: MvPoLineForm = {
    ...emptyMvLine(locationCode),
    client_id:
      typeof raw.client_id === "string" && raw.client_id.trim() !== ""
        ? raw.client_id
        : newDraftLineClientId(),
    sku_split_group_id:
      typeof raw.sku_split_group_id === "string" && raw.sku_split_group_id.trim() !== ""
        ? raw.sku_split_group_id
        : null,
    sku: typeof raw.sku === "string" ? raw.sku : "",
    line_description: typeof raw.line_description === "string" ? raw.line_description : "",
    quantity: typeof raw.quantity === "string" ? raw.quantity : raw.quantity != null ? String(raw.quantity) : "",
    unit_price: typeof raw.unit_price === "string" ? raw.unit_price : raw.unit_price != null ? String(raw.unit_price) : "",
    mv_row_detail_id:
      typeof raw.mv_row_detail_id === "string"
        ? raw.mv_row_detail_id
        : raw.mv_row_detail_id != null
          ? String(raw.mv_row_detail_id)
          : "",
    expected_delivery_date:
      typeof raw.expected_delivery_date === "string" ? raw.expected_delivery_date : "",
    transfer_requests: [],
  };

  if (Array.isArray(raw.transfer_requests)) {
    base.transfer_requests = raw.transfer_requests.map((entry) => {
      const transfer = entry as Partial<MvPoLineTransferRequestForm> & Record<string, unknown>;
      return {
        client_id:
          typeof transfer.client_id === "string" && transfer.client_id.trim() !== ""
            ? transfer.client_id
            : newTransferRequestClientId(),
        quantity: typeof transfer.quantity === "string" ? transfer.quantity : "",
        destination_location_id:
          typeof transfer.destination_location_id === "string" ? transfer.destination_location_id : "",
        target_date: typeof transfer.target_date === "string" ? transfer.target_date : "",
        reason:
          typeof transfer.reason === "string" && transfer.reason.trim() !== ""
            ? (transfer.reason as MrpTransferReason)
            : "upcoming_needs",
        notes: typeof transfer.notes === "string" ? transfer.notes : "",
      };
    });
    return sanitizeMvPoLineForm(base);
  }

  if (raw.transfer_request_enabled === true) {
    base.transfer_requests = [
      {
        client_id: newTransferRequestClientId(),
        quantity:
          typeof raw.transfer_quantity === "string" && raw.transfer_quantity.trim() !== ""
            ? raw.transfer_quantity
            : base.quantity,
        destination_location_id:
          typeof raw.transfer_destination_location_id === "string" &&
          raw.transfer_destination_location_id.trim() !== ""
            ? raw.transfer_destination_location_id
            : String(defaultPurchaseOrderTransferDestinationPublicLocationId(locationCode)),
        target_date:
          typeof raw.transfer_target_date === "string" && raw.transfer_target_date.trim() !== ""
            ? raw.transfer_target_date
            : base.expected_delivery_date,
        reason: "upcoming_needs",
        notes: typeof raw.transfer_notes === "string" ? raw.transfer_notes : "",
      },
    ];
  }

  return sanitizeMvPoLineForm(base);
}

export function emptyMvLine(_locationCode: string = "sgs"): MvPoLineForm {
  return {
    client_id: newDraftLineClientId(),
    sku_split_group_id: null,
    sku: "",
    line_description: "",
    quantity: "",
    unit_price: "",
    mv_row_detail_id: "",
    expected_delivery_date: "",
    transfer_requests: [],
  };
}

/** Coerce nullable API / autosave values so render paths can call trim safely. */
export function sanitizeMvPoLineForm(line: MvPoLineForm): MvPoLineForm {
  return {
    ...line,
    sku: trimText(line.sku),
    line_description: trimText(line.line_description),
    quantity: trimText(line.quantity),
    unit_price: trimText(line.unit_price),
    mv_row_detail_id: trimText(line.mv_row_detail_id),
    expected_delivery_date: trimText(line.expected_delivery_date),
    transfer_requests: line.transfer_requests.map((transfer) => ({
      ...transfer,
      quantity: trimText(transfer.quantity),
      destination_location_id: trimText(transfer.destination_location_id),
      target_date: trimText(transfer.target_date),
      notes: trimText(transfer.notes),
    })),
  };
}

export function sanitizeMvPoLineForms(lines: MvPoLineForm[]): MvPoLineForm[] {
  return lines.map(sanitizeMvPoLineForm);
}

export function updateDraftLineAt(
  lines: MvPoLineForm[],
  lineIndex: number,
  patch: Partial<MvPoLineForm>,
): MvPoLineForm[] {
  const next = [...lines];
  next[lineIndex] = { ...next[lineIndex]!, ...patch };
  if (typeof patch.sku === "string") {
    const splitGroupId = trimText(next[lineIndex]!.sku_split_group_id);
    if (splitGroupId !== "") {
      for (let index = 0; index < next.length; index += 1) {
        if (index === lineIndex) continue;
        if (next[index]!.sku_split_group_id === splitGroupId) {
          next[index] = { ...next[index]!, sku: patch.sku };
        }
      }
    }
  }
  return sanitizeMvPoLineForms(next);
}

export function removeDraftLineAt(lines: MvPoLineForm[], lineIndex: number): MvPoLineForm[] {
  const removed = lines[lineIndex];
  if (removed == null) return lines;
  const next = lines.filter((_, index) => index !== lineIndex);
  const splitGroupId = removed.sku_split_group_id?.trim();
  if (splitGroupId != null && splitGroupId !== "") {
    const remainingInGroup = next.filter((line) => line.sku_split_group_id === splitGroupId);
    if (remainingInGroup.length === 1) {
      return next.map((line) =>
        line.sku_split_group_id === splitGroupId ? { ...line, sku_split_group_id: null } : line,
      );
    }
  }
  return next;
}

export function addCommercialSplitLineAfter(
  lines: MvPoLineForm[],
  afterIndex: number,
  locationCode: string,
): MvPoLineForm[] {
  const source = lines[afterIndex];
  if (source == null) return lines;
  const sku = trimText(source.sku);
  if (sku === "") return lines;
  const splitGroupId = source.sku_split_group_id?.trim() || newDraftLineClientId();
  const newLine: MvPoLineForm = {
    ...emptyMvLine(locationCode),
    sku,
    sku_split_group_id: splitGroupId,
    line_description: source.line_description,
    mv_unit: source.mv_unit ?? null,
  };
  const next = lines.map((line, index) =>
    index === afterIndex ? { ...line, sku_split_group_id: splitGroupId } : line,
  );
  next.splice(afterIndex + 1, 0, newLine);
  return next;
}

/** Allow digits and at most one decimal separator while the operator types a unit price. */
export function sanitizeDraftUnitPriceInput(raw: string): string {
  let sanitized = raw.replace(/[^\d.]/g, "");
  const dotIndex = sanitized.indexOf(".");
  if (dotIndex >= 0) {
    sanitized =
      sanitized.slice(0, dotIndex + 1) + sanitized.slice(dotIndex + 1).replace(/\./g, "");
  }
  return sanitized;
}

/**
 * Format a draft unit price for display: always at least two decimal places (1 → 1.00),
 * but preserve extra precision when the operator entered it (4.802 stays 4.802).
 */
export function formatDraftUnitPriceDisplay(raw: string): string {
  const trimmed = raw.trim();
  if (trimmed === "") return "";
  const withoutTrailingDot = trimmed.endsWith(".") ? trimmed.slice(0, -1) : trimmed;
  if (withoutTrailingDot === "") return "";
  const parsed = Number.parseFloat(withoutTrailingDot);
  if (!Number.isFinite(parsed)) return trimmed;
  if (!withoutTrailingDot.includes(".")) {
    return parsed.toFixed(2);
  }
  const fractionalLength = withoutTrailingDot.split(".")[1]!.length;
  return parsed.toFixed(Math.max(2, fractionalLength));
}

/** Format a saved unit price so mill fractions stay visible (0.0256 stays 0.0256). */
export function formatSavedUnitPrice(value: number | string | null | undefined): string {
  if (value == null) return "";
  if (typeof value === "string") {
    return formatDraftUnitPriceDisplay(value);
  }
  if (!Number.isFinite(value)) return "";
  const raw = value.toFixed(8).replace(/\.?0+$/, "");
  if (!raw.includes(".")) {
    return `${raw}.00`;
  }
  const fractionalLength = raw.split(".")[1]!.length;
  if (fractionalLength < 2) {
    return value.toFixed(2);
  }
  return raw;
}

export async function prefillLineDescriptionFromSkuCatalog(
  sku: string,
  currentDescription: string,
): Promise<string> {
  if (currentDescription.trim() !== "" || sku.trim() === "") {
    return currentDescription;
  }
  try {
    const res = await apiFetch<{ sku_hover_hints: Record<string, MrpSkuHoverHintFields> }>(
      "/api/mrp/sku-hover-hints",
      { query: { cpns: sku.trim() } },
    );
    const hint = res.sku_hover_hints[sku.trim()];
    return buildMrpSkuCatalogLineDescription(hint) ?? currentDescription;
  } catch {
    return currentDescription;
  }
}

/** True when the SKU is a Megaventory service (labour / non-physical). */
export { isPurchaseOrderServiceSku, formatServiceSkuUnitPriceAutofill };

export type PurchaseOrderSkuCatalogPrefill = {
  line_description: string;
  /** Set when a service SKU has a positive landed unit cost and the line price was blank. */
  unit_price?: string;
  mv_unit?: string | null;
};

/**
 * Prefill line description from catalog, and unit price for service SKUs only (`mv_unit_cost`).
 * Does not overwrite a non-empty unit price the operator already typed.
 */
export async function prefillPurchaseOrderLineFromSkuCatalog(
  sku: string,
  current: { line_description: string; unit_price: string },
): Promise<PurchaseOrderSkuCatalogPrefill> {
  const trimmedSku = sku.trim();
  if (trimmedSku === "") {
    return { line_description: current.line_description };
  }

  let line_description = current.line_description;
  if (line_description.trim() === "") {
    line_description = await prefillLineDescriptionFromSkuCatalog(trimmedSku, line_description);
  }

  const out: PurchaseOrderSkuCatalogPrefill = { line_description };
  if (trimText(current.unit_price) !== "") {
    return out;
  }

  try {
    const detail = await apiFetch<MrpSkuDetail>("/api/mrp/skus", {
      query: { sku: trimmedSku },
    });
    const autofill = formatServiceSkuUnitPriceAutofill(detail);
    if (autofill != null) {
      out.unit_price = autofill;
    }
    out.mv_unit = trimTextOrNull(detail.mv_unit);
  } catch {
    // Description prefill already applied; price stays blank.
  }
  return out;
}

function mergeDraftLineIntoForm(
  draftLine: PurchaseOrderDraftLineResponse,
  existingLine: MvPoLineForm | undefined,
  locationCode: string,
): MvPoLineForm {
  const draftExpectedDelivery = trimText(draftLine.expected_delivery_date);
  const base: MvPoLineForm = {
    ...emptyMvLine(locationCode),
    sku: trimText(draftLine.sku),
    line_description: trimText(draftLine.line_description),
    quantity: String(draftLine.quantity),
    unit_price: draftLine.unit_price === 0 ? "" : String(draftLine.unit_price),
    mv_row_detail_id: String(draftLine.mv_row_detail_id ?? ""),
    expected_delivery_date: draftExpectedDelivery,
  };
  if (existingLine == null) {
    return sanitizeMvPoLineForm(base);
  }
  return sanitizeMvPoLineForm({
    ...base,
    client_id: existingLine.client_id,
    sku_split_group_id: existingLine.sku_split_group_id,
    line_description:
      trimText(existingLine.line_description) !== "" ? trimText(existingLine.line_description) : base.line_description,
    expected_delivery_date:
      trimText(existingLine.expected_delivery_date) !== ""
        ? trimText(existingLine.expected_delivery_date)
        : base.expected_delivery_date,
    transfer_requests: existingLine.transfer_requests,
  });
}

export function mergeDraftLinesIntoForm(
  draftLines: PurchaseOrderDraftLineResponse[],
  currentLines: MvPoLineForm[],
  locationCode: string,
): MvPoLineForm[] {
  if (draftLines.length === 0) {
    return [emptyMvLine(locationCode)];
  }
  const usedIndices = new Set<number>();
  return draftLines.map((draftLine, index) => {
    const detailId =
      draftLine.mv_row_detail_id != null && String(draftLine.mv_row_detail_id).trim() !== ""
        ? String(draftLine.mv_row_detail_id)
        : "";
    let existingIndex = -1;
    if (index < currentLines.length && !usedIndices.has(index)) {
      existingIndex = index;
    } else if (detailId !== "") {
      existingIndex = currentLines.findIndex(
        (line, lineIndex) =>
          !usedIndices.has(lineIndex) && line.mv_row_detail_id?.trim() === detailId,
      );
    }
    if (existingIndex < 0) {
      const skuNeedle = trimText(draftLine.sku).toLowerCase();
      existingIndex = currentLines.findIndex(
        (line, lineIndex) =>
          !usedIndices.has(lineIndex) && trimText(line.sku).toLowerCase() === skuNeedle,
      );
    }
    if (existingIndex < 0 && index < currentLines.length && !usedIndices.has(index)) {
      existingIndex = index;
    }
    if (existingIndex >= 0) {
      usedIndices.add(existingIndex);
    }
    const existingLine = existingIndex >= 0 ? currentLines[existingIndex] : undefined;
    return mergeDraftLineIntoForm(draftLine, existingLine, locationCode);
  });
}

export function purchaseOrderLinesToDraftForm(
  lines: MrpPurchaseOrderLine[],
  locationCode: string,
): MvPoLineForm[] {
  if (lines.length === 0) {
    return [emptyMvLine(locationCode)];
  }
  const rows = lines.map((line) => ({
    ...emptyMvLine(locationCode),
    sku: trimText(line.cpn),
    line_description: trimText(line.description),
    quantity: String(line.quantity_ordered),
    unit_price: line.unit_price == null ? "" : String(line.unit_price),
    mv_row_detail_id: line.mv_row_detail_id != null ? String(line.mv_row_detail_id) : "",
    expected_delivery_date: trimText(line.expected_arrival),
    purchase_order_item_id: line.id,
  }));
  return sanitizeMvPoLineForms(assignSkuSplitGroupIdsForDuplicateSkus(rows));
}

export function purchaseOrderToDraftFormHeader(purchaseOrder: MrpPurchaseOrder): {
  currency: PurchaseOrderCurrency;
  paymentTermsEnumeration: string;
  comments: string;
} {
  const normalizedCurrency = trimText(purchaseOrder.currency).toUpperCase();
  const paymentTerms = trimText(purchaseOrder.payment_terms);
  return {
    currency: normalizedCurrency === "CNY" ? "CNY" : "USD",
    paymentTermsEnumeration: paymentTerms !== "" ? paymentTerms : "None",
    comments: trimText(purchaseOrder.special_instructions),
  };
}

export function buildPurchaseOrderSubmitLines(
  lines: MvPoLineForm[],
  locationCode: string,
): PurchaseOrderSubmitLine[] {
  const transfersBySku = new Map<
    string,
    NonNullable<PurchaseOrderSubmitLine["transfer_requests"]>
  >();
  for (const line of lines) {
    const sku = trimText(line.sku);
    if (sku === "" || line.transfer_requests.length === 0) continue;
    const expectedDelivery = trimText(line.expected_delivery_date);
    const mapped = line.transfer_requests.map((transferRequest) => ({
      quantity: Number.parseInt(transferRequest.quantity || line.quantity || "0", 10),
      origin_location_id: defaultPurchaseOrderTransferOriginPublicLocationId(locationCode),
      destination_location_id: Number.parseInt(transferRequest.destination_location_id, 10),
      target_date: trimText(transferRequest.target_date) || expectedDelivery,
      reason: transferRequest.reason,
      notes: trimTextOrNull(transferRequest.notes),
    }));
    const key = normalizePurchaseOrderSku(sku);
    const existing = transfersBySku.get(key) ?? [];
    transfersBySku.set(key, [...existing, ...mapped]);
  }

  const firstSkuSeen = new Set<string>();
  return lines
    .map((line) => {
      const sku = trimText(line.sku);
      if (sku === "") return null;
      const idRaw = trimText(line.mv_row_detail_id);
      const mvRowDetailId = idRaw === "" ? undefined : Number.parseInt(idRaw, 10);
      const expectedDelivery = trimText(line.expected_delivery_date);
      const lineDescription = trimText(line.line_description);
      const submitLine: PurchaseOrderSubmitLine = {
        sku,
        quantity: Number.parseFloat(line.quantity || "0"),
        unit_price: trimText(line.unit_price) === "" ? null : Number.parseFloat(line.unit_price),
        ...(lineDescription.length > 0 ? { line_description: lineDescription } : {}),
        mv_row_detail_id:
          mvRowDetailId != null && Number.isFinite(mvRowDetailId) && mvRowDetailId > 0
            ? mvRowDetailId
            : undefined,
      };
      if (expectedDelivery !== "") {
        submitLine.expected_delivery_date = expectedDelivery;
      }
      const skuKey = normalizePurchaseOrderSku(sku);
      if (!firstSkuSeen.has(skuKey)) {
        firstSkuSeen.add(skuKey);
        const transferRequests = transfersBySku.get(skuKey);
        if (transferRequests != null && transferRequests.length > 0) {
          submitLine.transfer_requests = transferRequests;
        }
      }
      return submitLine;
    })
    .filter((line): line is PurchaseOrderSubmitLine => line != null);
}

export function validatePurchaseOrderSubmitLines(bodyLines: PurchaseOrderSubmitLine[]): string | null {
  if (bodyLines.length === 0) return "Add at least one line with a SKU.";
  for (const line of bodyLines) {
    if (!Number.isFinite(line.quantity) || line.quantity <= 0) {
      return `Quantity must be positive for ${line.sku}.`;
    }
    if (line.unit_price != null && !Number.isFinite(line.unit_price)) {
      return `Unit price is invalid for ${line.sku}.`;
    }
    if (line.transfer_requests != null) {
      for (const transferRequest of line.transfer_requests) {
        if (!Number.isFinite(transferRequest.quantity) || transferRequest.quantity <= 0) {
          return `Transfer request quantity must be positive for ${line.sku}.`;
        }
        if (transferRequest.origin_location_id === transferRequest.destination_location_id) {
          return `Transfer request origin and destination must differ for ${line.sku}.`;
        }
        if (trimText(transferRequest.target_date) === "") {
          return `Transfer request target date is required for ${line.sku}.`;
        }
      }
    }
  }
  return null;
}

export function validateDraftPurchaseOrderLines(lines: MvPoLineForm[]): string | null {
  const duplicateLineIndices = findDraftAccidentalDuplicateSkuLineIndices(lines);
  if (duplicateLineIndices.size > 0) {
    return "Each SKU can only appear once unless you use + beside the SKU to add another commercial line for that SKU.";
  }
  return null;
}

export function purchaseOrderCurrencyFromSupplier(value: string | null | undefined): PurchaseOrderCurrency {
  const normalized = value?.trim().toUpperCase() ?? "";
  if (normalized === "CNY") return "CNY";
  return "USD";
}

export function paymentTermsMvFromSupplier(value: string | null | undefined): string {
  const trimmed = value?.trim() ?? "";
  return trimmed.length > 0 ? trimmed : "None";
}

export function draftSupplierDataIdForPurchaseOrder(purchaseOrder: MrpPurchaseOrder): number | null {
  return purchaseOrder.draft_supplier_data_id ?? purchaseOrder.supplier_id;
}

/** Hub-only compose shell before supplier is chosen (no Megaventory row yet). */
export function buildEmptyComposePurchaseOrder(locationCode: string): MrpPurchaseOrder {
  return {
    id: 0,
    purchase_order_mv_id: null,
    po_number: null,
    supplier: null,
    location_code: locationCode,
    mv_status: "Pending",
    supplier_id: null,
    currency: "USD",
    payment_terms: "None",
    amount_rounding_mode: "line",
    supplier_payment_terms: null,
    special_instructions: null,
    line_count: 0,
    quantity_ordered: 0,
    order_total: 0,
    quantity_received: 0,
    remaining_quantity: 0,
    next_expected_arrival: null,
    status: "open",
    mv_created_at: "",
    updated_at: "",
    cpns: [],
    line_rows: [],
    is_draft: true,
    draft_owner_user_id: null,
    draft_owner_email: null,
    draft_owner_display_name: null,
    draft_supplier_data_id: null,
    draft_location_code: locationCode,
    can_edit_draft: true,
  };
}

/** Hub-only compose view before the first Megaventory write (Save draft / Place order). */
export function buildLocalComposePurchaseOrder(input: {
  supplierDataId: number;
  supplierName: string | null;
  supplierCurrency: string | null;
  supplierPaymentTerms: string | null;
  locationCode: string;
  paymentTermsEnumeration: string;
}): MrpPurchaseOrder {
  const currency = purchaseOrderCurrencyFromSupplier(input.supplierCurrency);
  return {
    id: 0,
    purchase_order_mv_id: null,
    po_number: null,
    supplier: input.supplierName,
    location_code: input.locationCode,
    mv_status: "Pending",
    supplier_id: input.supplierDataId,
    currency,
    payment_terms: input.paymentTermsEnumeration,
    amount_rounding_mode: "line",
    supplier_payment_terms: paymentTermsMvFromSupplier(input.supplierPaymentTerms),
    special_instructions: null,
    line_count: 0,
    quantity_ordered: 0,
    order_total: 0,
    quantity_received: 0,
    remaining_quantity: 0,
    next_expected_arrival: null,
    status: "open",
    mv_created_at: "",
    updated_at: "",
    cpns: [],
    line_rows: [],
    is_draft: true,
    draft_owner_user_id: null,
    draft_owner_email: null,
    draft_owner_display_name: null,
    draft_supplier_data_id: input.supplierDataId,
    draft_location_code: input.locationCode,
    can_edit_draft: true,
  };
}

export const PURCHASE_ORDER_DRAFT_COMPOSE_HOVER_HINT =
  "Edit lines, expected delivery dates, and optional transfer requests. The hub autosaves your draft as you work. Approve writes the pending purchase order, assigns a PO number, and locks the supplier.";

export function hubComposeDraftLinesFromApi(
  lines: MvPoLineForm[],
  locationCode: string,
): MvPoLineForm[] {
  if (!Array.isArray(lines) || lines.length === 0) {
    return [emptyMvLine(locationCode)];
  }
  return lines.map((line) => sanitizeMvPoLineForm(normalizeMvPoLineForm(line as Partial<MvPoLineForm> & Record<string, unknown>, locationCode)));
}

export function parseDraftComposeLocation(raw: string | null): MrpLocation | null {
  if (raw === "sgs" || raw === "suntronic" || raw === "kodiak") {
    return raw;
  }
  return null;
}

export function parseDraftComposePrefillLines(
  raw: string | null,
  locationCode: string,
): MvPoLineForm[] | null {
  if (raw == null || raw.trim() === "") {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as {
      sku: string;
      quantity: number;
      unit_price: number | null;
    }[];
    if (!Array.isArray(parsed) || parsed.length === 0) {
      return null;
    }
    return parsed.map((line) =>
      sanitizeMvPoLineForm({
        ...emptyMvLine(locationCode),
        sku: trimText(line.sku),
        quantity: line.quantity != null ? String(line.quantity) : "1",
        unit_price:
          line.unit_price != null && Number.isFinite(line.unit_price) ? String(line.unit_price) : "",
      }),
    );
  } catch {
    return null;
  }
}

/** True when an unsaved saved-mode PO row can be posted (SKU required; quantity and price optional). */
export function unsavedPurchaseOrderLineReadyToCommit(line: {
  sku: string;
  quantity: string;
  unit_price: string;
}): boolean {
  return trimText(line.sku) !== "";
}

export function draftComposeOrderTotal(
  lines: MvPoLineForm[],
  mode: PurchaseOrderAmountRoundingMode = parsePurchaseOrderAmountRoundingMode(null),
): number {
  const priced: Array<{ quantity: number; unitPrice: number }> = [];
  for (const line of lines) {
    const quantity = Number.parseFloat(line.quantity);
    const unitPrice = Number.parseFloat(line.unit_price);
    if (Number.isFinite(quantity) && Number.isFinite(unitPrice)) {
      priced.push({ quantity, unitPrice });
    }
  }
  return purchaseOrderOrderTotal(priced, mode);
}
